class AudioManager {
  constructor() {
    this.muted = false;
    this.sounds = {};
    this.context = null;
    this.currentMusic = null;
    this.audioUnlocked = false;

    // Music tracks
    this.music = {
      title: this.createMusic('https://cdn.prod.website-files.com/64aa957da446d8dc44869f81/691be24f3d81d943f5f7743f_music_title.mp3', 0.6),
      cloaks1: this.createMusic('https://cdn.prod.website-files.com/64aa957da446d8dc44869f81/691be252106d0cfd50b9bbdf_music_cloaks_1.mp3', 0.6),
      cloaks2: this.createMusic('https://cdn.prod.website-files.com/64aa957da446d8dc44869f81/691be250e300065e19f523a6_music_cloaks_2.mp3', 0.6),
      migos1: this.createMusic('https://cdn.prod.website-files.com/64aa957da446d8dc44869f81/691be251f6b2371bcf53cde5_music_migos_1.mp3', 0.6),
      migos2: this.createMusic('https://cdn.prod.website-files.com/64aa957da446d8dc44869f81/691be250cf54a51e015f025c_music_migos_2.mp3', 0.6),
      boss: this.createMusic('https://cdn.prod.website-files.com/64aa957da446d8dc44869f81/691be25070a723985689cb04_music_boss.mp3', 0.7)
    };

    this.setupAudioUnlock();
  }

  createMusic(src, volume) {
    const audio = new Audio(src);
    audio.loop = true;
    audio.baseVolume = volume;
    audio.volume = volume;

    // Fallback: manually restart if loop fails
    audio.addEventListener('ended', () => {
      if (this.currentMusic === audio && !this.muted) {
        audio.currentTime = 0;
        audio.play().catch(err => {
          console.warn('Music loop restart failed:', err);
        });
      }
    });

    return audio;
  }

  setupAudioUnlock() {
    const unlock = () => {
      if (this.audioUnlocked) return;
      this.audioUnlocked = true;

      // Resume audio context
      if (this.context && this.context.state === 'suspended') {
        this.context.resume().catch(() => {});
      }

      if (this.currentMusic && !this.muted) {
        this.currentMusic.play().catch(() => {});
      }
      window.removeEventListener('pointerdown', unlock);
      window.removeEventListener('keydown', unlock);
    };
    window.addEventListener('pointerdown', unlock);
    window.addEventListener('keydown', unlock);

    // Mobile: keep trying to resume music on touch interactions during gameplay
    const keepMusicAlive = () => {
      if (this.currentMusic && this.currentMusic.paused && !this.muted && this.audioUnlocked) {
        if (this.context && this.context.state === 'suspended') {
          this.context.resume().catch(() => {});
        }
        this.currentMusic.play().catch(() => {});
      }
    };
    // Listen for ongoing touch events to keep music playing on mobile
    window.addEventListener('touchstart', keepMusicAlive, { passive: true });
    window.addEventListener('touchend', keepMusicAlive, { passive: true });
  }

  playMusic(trackName) {
    const nextTrack = this.music[trackName];
    if (!nextTrack) return;

    // If switching tracks, stop current music completely
    if (this.currentMusic && this.currentMusic !== nextTrack) {
      this.currentMusic.pause();
      this.currentMusic.currentTime = 0;
    }

    this.currentMusic = nextTrack;

    // Always ensure correct volume is set
    this.currentMusic.volume = this.muted ? 0 : this.currentMusic.baseVolume;

    // Ensure loop is enabled
    this.currentMusic.loop = true;

    // Reset to beginning if track was stopped
    if (this.currentMusic.currentTime > 0 && this.currentMusic.paused) {
      this.currentMusic.currentTime = 0;
    }

    // Resume audio context if suspended (mobile browser requirement)
    if (this.context && this.context.state === 'suspended') {
      this.context.resume().catch(() => {});
    }

    // Play or resume the track (with retry for mobile)
    const playWithRetry = () => {
      this.currentMusic.play().catch(err => {
        console.warn('Music playback failed, will retry on interaction:', err);
        // On mobile, we may need user interaction - setupAudioUnlock will handle it
      });
    };

    if (this.currentMusic.paused) {
      playWithRetry();

      // Mobile fallback: retry after a short delay
      setTimeout(() => {
        if (this.currentMusic && this.currentMusic.paused && !this.muted) {
          playWithRetry();
        }
      }, 100);
    }
  }

  stopMusic() {
    if (this.currentMusic) {
      this.currentMusic.pause();
      this.currentMusic.currentTime = 0;
    }
  }

  init() {
    try {
      this.context = new (window.AudioContext || window.webkitAudioContext)();
    } catch (e) {
      console.warn('Web Audio API not supported');
    }

    // Set title music to be played after user interaction
    this.currentMusic = this.music.title;
    this.currentMusic.volume = this.muted ? 0 : this.currentMusic.baseVolume;

    // Handle page visibility changes (tab switching, screen lock, etc.)
    document.addEventListener('visibilitychange', () => {
      if (!document.hidden && !this.muted && this.currentMusic) {
        // Resume audio context if suspended
        if (this.context && this.context.state === 'suspended') {
          this.context.resume().catch(() => {});
        }
        // Resume music playback if paused
        if (this.currentMusic.paused) {
          this.currentMusic.play().catch(() => {});
        }
      }
    });

    // Music will play automatically when user clicks/taps due to setupAudioUnlock()
  }

  playSound(frequency, duration = 100) {
    if (this.muted || !this.context) return;

    try {
      const oscillator = this.context.createOscillator();
      const gainNode = this.context.createGain();

      oscillator.connect(gainNode);
      gainNode.connect(this.context.destination);

      oscillator.frequency.value = frequency;
      oscillator.type = 'square';

      gainNode.gain.setValueAtTime(0.1, this.context.currentTime);
      gainNode.gain.exponentialRampToValueAtTime(
        0.01,
        this.context.currentTime + duration / 1000
      );

      oscillator.start(this.context.currentTime);
      oscillator.stop(this.context.currentTime + duration / 1000);
    } catch (e) {
      console.warn('Error playing sound:', e);
    }
  }

  shoot() {
    this.playSound(800, 50);
  }

  hit() {
    this.playSound(200, 100);
  }

  powerup() {
    this.playSound(1200, 150);
  }

  explosion() {
    this.playSound(100, 200);
  }

  levelComplete() {
    this.playSound(1500, 300);
  }

  gameOver() {
    // Play distinctive game over sound (low descending tone)
    this.playSound(150, 400);
  }

  toggleMute() {
    this.muted = !this.muted;

    // Update all music track volumes
    Object.values(this.music).forEach(track => {
      track.volume = this.muted ? 0 : track.baseVolume;
    });

    // If unmuting and there's a current track, ensure it's playing
    if (!this.muted && this.currentMusic) {
      // Ensure loop is enabled
      this.currentMusic.loop = true;

      // Resume audio context if suspended
      if (this.context && this.context.state === 'suspended') {
        this.context.resume().catch(() => {});
      }

      // Resume playback if paused
      if (this.currentMusic.paused) {
        this.currentMusic.play().catch(err => {
          console.warn('Music resume failed:', err);
        });
      }
    }

    return this.muted;
  }

  isMuted() {
    return this.muted;
  }
}

export const audioManager = new AudioManager();
